/**
 * Lists that come in pages (1.0.0-rc.3): the server returns `nextCursor` when more rows follow, and the page shows a
 * Load more button. The hook keeps the shape every list used before ({ data: { items } }), with the loaded pages
 * joined, so components only add the button.
 */
import { useMemo } from 'react';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import { api } from '../client';

export interface Page<T> { items: T[]; nextCursor?: string | null }

/**
 * 1.8.1: the refetch interval for the loaded pages. Only while one page is loaded (as useMigrationItems does): a
 * refetch of an infinite query fetches every loaded page again, so a list scrolled deep would read them all each time.
 */
export function pagedInterval<T>(
  refetchInterval: number | false | ((items: T[]) => number | false) | undefined, pages: Array<Page<T>>,
): number | false | undefined {
  if (pages.length > 1) return false;
  return typeof refetchInterval === 'function' ? refetchInterval(pages.flatMap((p) => p.items)) : refetchInterval;
}

export function usePaged<T, P extends Page<T> = Page<T>>(opts: {
  queryKey: QueryKey;
  /** The list's URL without paging parameters. */
  url: string;
  pageSize?: number;
  enabled?: boolean;
  /** A fixed interval, or one chosen from all the loaded rows; only while one page is loaded (1.8.1). */
  refetchInterval?: number | false | ((items: T[]) => number | false);
  /** How to join pages (default: their items). */
  join?: (pages: P[]) => P;
}) {
  const { queryKey, url, pageSize = 100, enabled = true, refetchInterval, join } = opts;
  const q = useInfiniteQuery({
    queryKey: [...queryKey, 'paged', pageSize],
    queryFn: ({ pageParam }) => {
      const u = new URL(url, 'http://x');
      u.searchParams.set('limit', String(pageSize));
      if (pageParam) u.searchParams.set('cursor', pageParam);
      return api<P>(`${u.pathname}${u.search}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    refetchInterval: (query) => pagedInterval<T>(refetchInterval, query.state.data?.pages ?? []) ?? false,
  });
  const data = useMemo(() => {
    const pages = q.data?.pages;
    if (!pages?.length) return undefined;
    return join ? join(pages) : ({ ...pages[0]!, items: pages.flatMap((p) => p.items), nextCursor: pages.at(-1)!.nextCursor ?? null } as P);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);
  return { ...q, data, hasMore: Boolean(q.hasNextPage), loadMore: () => void q.fetchNextPage(), loadingMore: q.isFetchingNextPage };
}
